import { CheckCircle2, Clock3, Flag, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  isRunActive,
  type RenderRunActivity,
} from "@/features/credentials/components/primary-source-runs/types";
import { RunConcerns } from "@/features/credentials/components/primary-source-runs/run-summary";
import { concernCount } from "@/features/credentials/components/primary-source-runs/run-report-dialog/item-status";

export function renderWorkspaceRunActivity({
  run,
  summary,
  checks,
  itemSummary,
}: Parameters<RenderRunActivity>[0]): ReturnType<RenderRunActivity> {
  if (!isRunActive(run)) {
    if (run.status !== "completed" || itemSummary.total === 0) {
      return null;
    }
    return renderFinishedRun(itemSummary);
  }
  let label = "Verification in progress";
  let loading = true;
  let hint =
    "Results update automatically. You can leave this report and return later.";
  if (run.status === "queued") {
    label = "Starting verification";
    hint = "Your request is queued. Source checks will start automatically.";
  } else if (checks?.length) {
    const running = checks.some((check) => check.status === "running");
    const queued = checks.some((check) => check.status === "queued");
    const retrying = checks.some((check) => check.status === "retry-wait");
    if (!running && !queued && retrying) {
      label = "Waiting to retry";
      loading = false;
      hint =
        "Remaining checks will retry automatically. Open a source for its scheduled time.";
    } else if (!running && queued) {
      label = "Checks queued";
      loading = false;
      hint =
        "Waiting for the next source checks to start. Results update automatically.";
    } else if (!running && !queued && !retrying) {
      label = "Finishing verification";
    }
  }
  const Icon = loading ? Loader2 : Clock3;
  return {
    label: "Verification",
    badge: (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-800">
        <Icon
          className={cn(
            "size-3.5 shrink-0",
            loading && "motion-safe:animate-spin",
          )}
          aria-hidden
        />
        {label}
      </span>
    ),
    details: (
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="space-y-1 text-xs leading-5"
      >
        <p className="font-medium text-blue-800">
          {summary.collectedChecks} of {summary.totalChecks} checks collected
          {summary.pendingChecks > 0 && ` · ${summary.pendingChecks} pending`}
        </p>
        <p className="text-muted-foreground">{hint}</p>
      </div>
    ),
  };
}

/** A finished run's review: green only when every item was accepted. Concerns
 * are named so the row says what staff flagged without opening the report. */
function renderFinishedRun(
  itemSummary: Parameters<RenderRunActivity>[0]["itemSummary"],
): ReturnType<RenderRunActivity> {
  const reviewed = itemSummary.accepted + itemSummary.concern;
  let Icon = Clock3;
  let style = "bg-amber-50 text-amber-900";
  let text = `${reviewed} of ${itemSummary.total} items reviewed`;
  if (itemSummary.complete && itemSummary.concern > 0) {
    Icon = Flag;
    style = "bg-red-50 text-red-800";
    text = `Complete · ${concernCount(itemSummary.concern)}`;
  } else if (itemSummary.complete) {
    Icon = CheckCircle2;
    style = "bg-emerald-50 text-emerald-800";
    text = "Complete";
  }
  return {
    label: "Verification",
    showSourceFindings: false,
    badge: (
      <span
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-xs font-medium",
          style,
        )}
      >
        <Icon className="size-3.5" aria-hidden />
        {text}
      </span>
    ),
    details: <RunConcerns summary={itemSummary} />,
  };
}
